import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { SpinnerGap, TrendUp, ChartBar, Trophy, Star, CaretDown, CaretUp, Warning, UserMinus } from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import { fmtINR } from '../utils/currency';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const formatINR = (amount) => fmtINR(amount || 0);

const ModuleSection = ({ title, badge, badgeColor, borderColor, data, expanded, onToggle }) => {
  if (!data) return null;
  const approvalRate = data.total > 0 ? Math.round((data.approved_count / data.total) * 100) : 0;
  const topPerformer = Object.entries(data.by_creator || {})
    .sort((a, b) => b[1].total_incentive - a[1].total_incentive)[0];

  return (
    <Card className={`border-l-4 ${borderColor}`}>
      <div className="p-5 cursor-pointer" onClick={onToggle} data-testid={`toggle-${badge.toLowerCase()}`}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <h2 className="text-lg font-bold text-slate-900 font-['Outfit']">{title}</h2>
            <Badge className={`${badgeColor} text-sm px-3 py-1`}>{badge}</Badge>
          </div>
          {expanded ? <CaretUp size={18} className="text-slate-400" /> : <CaretDown size={18} className="text-slate-400" />}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {[
            { label: 'Total', val: data.total, bg: 'bg-slate-50', color: 'text-slate-900' },
            { label: 'Approved', val: data.approved_count, bg: 'bg-emerald-50', color: 'text-emerald-700' },
            { label: 'In Pipeline', val: data.pending_count, bg: 'bg-amber-50', color: 'text-amber-700' },
            { label: 'Rejected', val: data.rejected_count, bg: 'bg-red-50', color: 'text-red-700' },
            { label: 'Approval Rate', val: `${approvalRate}%`, bg: 'bg-blue-50', color: 'text-blue-700' },
          ].map(s => (
            <div key={s.label} className={`p-3 ${s.bg} rounded-lg text-center`}>
              <p className={`text-xl font-bold ${s.color}`}>{s.val}</p>
              <p className={`text-xs ${s.color} opacity-70`}>{s.label}</p>
            </div>
          ))}
        </div>
      </div>

      {expanded && (
        <CardContent className="pt-0 space-y-5 border-t border-slate-100">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 pt-4">
            <div className="p-4 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg border border-blue-100">
              <p className="text-sm text-blue-600 font-medium">Total Incentives (Approved)</p>
              <p className="text-2xl font-bold text-blue-800 mt-1">{formatINR(data.total_incentive)}</p>
            </div>
            <div className="p-4 bg-gradient-to-br from-emerald-50 to-teal-50 rounded-lg border border-emerald-100">
              <p className="text-sm text-emerald-600 font-medium">Avg Margin</p>
              <p className="text-2xl font-bold text-emerald-800 mt-1">${data.avg_margin}/hr</p>
            </div>
            <div className="p-4 bg-gradient-to-br from-purple-50 to-violet-50 rounded-lg border border-purple-100">
              <p className="text-sm text-purple-600 font-medium">Avg Bill Rate</p>
              <p className="text-2xl font-bold text-purple-800 mt-1">${data.avg_bill_rate}/hr</p>
            </div>
          </div>

          {topPerformer && topPerformer[1].total_incentive > 0 && (
            <div className="p-4 bg-gradient-to-r from-amber-50 to-yellow-50 rounded-lg border border-amber-200">
              <div className="flex items-center gap-3">
                <Trophy size={24} className="text-amber-600" />
                <div>
                  <p className="text-sm text-amber-700 font-medium">Top Performer</p>
                  <p className="text-lg font-bold text-amber-900">{topPerformer[0]}</p>
                  <p className="text-sm text-amber-600">{topPerformer[1].approved} approved — {formatINR(topPerformer[1].total_incentive)} total incentive</p>
                </div>
              </div>
            </div>
          )}

          {data.by_creator && Object.keys(data.by_creator).length > 0 && (
            <div>
              <h4 className="text-sm font-semibold text-slate-700 mb-2">Team Member Performance</h4>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid={`${badge.toLowerCase()}-team-table`}>
                  <thead>
                    <tr className="border-b border-slate-200 bg-slate-50">
                      <th className="text-left py-2 px-3 text-slate-500 font-medium">Name</th>
                      <th className="text-center py-2 px-3 text-slate-500 font-medium">Total</th>
                      <th className="text-center py-2 px-3 text-slate-500 font-medium">Approved</th>
                      <th className="text-right py-2 px-3 text-slate-500 font-medium">Total Incentive</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(data.by_creator)
                      .sort((a, b) => b[1].total_incentive - a[1].total_incentive)
                      .map(([name, stats]) => (
                        <tr key={name} className="border-b border-slate-100 hover:bg-slate-50">
                          <td className="py-2 px-3 font-medium text-slate-800">{name}</td>
                          <td className="py-2 px-3 text-center text-slate-600">{stats.total}</td>
                          <td className="py-2 px-3 text-center text-emerald-600 font-medium">{stats.approved}</td>
                          <td className="py-2 px-3 text-right font-bold text-slate-800">{formatINR(stats.total_incentive)}</td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
};

const CeoDashboardPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [salesExpanded, setSalesExpanded] = useState(true);
  const [recruitingExpanded, setRecruitingExpanded] = useState(true);
  const navigate = useNavigate();

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/ceo/dashboard`, { withCredentials: true });
      setData(response.data);
    } catch (error) {
      console.error('Failed to fetch CEO dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className="flex items-center justify-center py-20"><SpinnerGap size={32} className="animate-spin text-blue-600" /></div>;
  if (!data) return <div className="text-center py-12 text-slate-500">Failed to load dashboard data.</div>;

  const { overview, sales, recruiting, teams, users_by_role, total_users, no_placement_users, most_backouts } = data;

  return (
    <div className="space-y-6" data-testid="ceo-dashboard">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-['Outfit']">Executive Dashboard</h1>
          <p className="text-slate-500 mt-1">Complete executive view — {overview.current_quarter}</p>
        </div>
        <Button onClick={() => navigate('/dashboard/admin/payroll')} className="bg-emerald-600 hover:bg-emerald-700" data-testid="export-from-ceo">
          <ChartBar size={16} className="mr-2" /> Export Report
        </Button>
      </div>

      {/* Top-Level KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {[
          { label: 'Total Placements', val: overview.total_placements, color: 'text-slate-900', sub: null, testId: 'kpi-total' },
          { label: 'Approved', val: overview.total_approved, color: 'text-emerald-700', sub: null, testId: 'kpi-approved' },
          { label: 'In Pipeline', val: overview.total_pending, color: 'text-amber-700', sub: null, testId: 'kpi-pipeline' },
          { label: 'Rejected', val: overview.total_rejected, color: 'text-red-700', sub: null, testId: 'kpi-rejected' },
          { label: 'Total Incentive', val: formatINR(overview.total_incentive_approved), color: 'text-blue-800', sub: 'Approved', testId: 'kpi-incentive' },
          { label: 'Team Members', val: total_users, color: 'text-purple-700', sub: null, testId: 'kpi-members' },
        ].map(kpi => (
          <div key={kpi.testId} className="p-4 rounded-xl border bg-white" data-testid={kpi.testId}>
            <p className="text-xs text-slate-500">{kpi.label}</p>
            <p className={`text-2xl font-bold ${kpi.color}`}>{kpi.val}</p>
            {kpi.sub && <p className="text-[10px] text-slate-400">{kpi.sub}</p>}
          </div>
        ))}
      </div>

      {/* Ledger */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="border-emerald-200 bg-gradient-to-br from-emerald-50 to-white">
          <CardContent className="p-5">
            <p className="text-sm text-emerald-600 font-medium">Ledger — Pending Payout</p>
            <p className="text-3xl font-bold text-emerald-800 mt-2">{formatINR(overview.total_ledger_pending)}</p>
          </CardContent>
        </Card>
        <Card className="border-blue-200 bg-gradient-to-br from-blue-50 to-white">
          <CardContent className="p-5">
            <p className="text-sm text-blue-600 font-medium">Ledger — Paid Out</p>
            <p className="text-3xl font-bold text-blue-800 mt-2">{formatINR(overview.total_ledger_paid)}</p>
          </CardContent>
        </Card>
      </div>

      {/* People with No Placements + Most Backouts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="border-slate-200" data-testid="no-placement-users-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-['Outfit'] flex items-center gap-2">
              <UserMinus size={18} className="text-amber-600" /> People with No Placements
              <Badge variant="outline" className="text-[10px]">{(no_placement_users || []).length}</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {(no_placement_users || []).length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-4">All recruiters/leads have at least one placement</p>
            ) : (
              <div className="max-h-[500px] overflow-y-auto space-y-1">
                {no_placement_users.map((u, i) => (
                  <div key={i} className="flex items-center justify-between p-2 bg-red-50/60 rounded text-sm border border-red-100" data-testid={`no-placement-user-${i}`}>
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 rounded-full bg-red-200 flex items-center justify-center text-[10px] font-bold text-red-800">{u.name?.charAt(0)}</div>
                      <span className="font-medium text-red-800">{u.name}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="text-[9px] border-red-200 text-red-600">{u.role}</Badge>
                      <span className="text-xs text-red-500">{u.team || '-'}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="border-slate-200" data-testid="most-backouts-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-['Outfit'] flex items-center gap-2">
              <Warning size={18} className="text-red-600" /> Most Backouts
              <Badge variant="outline" className="text-[10px]">{(most_backouts || []).length} users</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {(most_backouts || []).length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-4">No backouts recorded</p>
            ) : (
              <div className="space-y-1.5">
                {most_backouts.map((u, i) => (
                  <div key={i} className="flex items-center justify-between p-2.5 rounded-lg bg-red-50/50" data-testid={`backout-user-${i}`}>
                    <div className="flex items-center gap-2">
                      <span className="w-6 h-6 rounded-full bg-red-200 text-red-800 text-xs font-bold flex items-center justify-center">{i + 1}</span>
                      <span className="font-medium text-slate-800 text-sm">{u.name}</span>
                    </div>
                    <Badge className="bg-red-100 text-red-700 text-xs">{u.count} backout{u.count > 1 ? 's' : ''}</Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Users & Teams Summary */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="border-slate-200">
          <CardHeader className="pb-3"><CardTitle className="text-base font-['Outfit']">Users by Role</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-1.5">
              {Object.entries(users_by_role || {}).map(([role, count]) => (
                <div key={role} className="flex items-center justify-between p-2 rounded bg-slate-50 text-sm">
                  <span className="text-slate-600">{role.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</span>
                  <span className="font-bold text-slate-800">{count}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardHeader className="pb-3"><CardTitle className="text-base font-['Outfit']">Teams Structure</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-2">
              {(teams || []).map((t, i) => (
                <div key={i} className="p-3 bg-slate-50 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-slate-700 text-sm">{t.name}</span>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded ${t.department === 'sales' ? 'bg-blue-100 text-blue-700' : 'bg-teal-100 text-teal-700'}`}>{t.department}</span>
                    </div>
                    <span className="text-xs text-slate-500">{t.member_count} members</span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Module Sections */}
      <ModuleSection title="Sales Teams" badge="SALES" badgeColor="bg-blue-100 text-blue-700" borderColor="border-l-blue-500"
        data={sales} expanded={salesExpanded} onToggle={() => setSalesExpanded(!salesExpanded)} />
      <ModuleSection title="Recruiting Teams" badge="RECRUITING" badgeColor="bg-teal-100 text-teal-700" borderColor="border-l-teal-500"
        data={recruiting} expanded={recruitingExpanded} onToggle={() => setRecruitingExpanded(!recruitingExpanded)} />
    </div>
  );
};

export default CeoDashboardPage;
